import type { LocationResult } from '../components/location-search/location-search.js'

const SOURCE_BOOSTS: Record<string, number> = {
  cotrex: 1.1,
  openbeta: 1.1,
  photon: 1.0,
}

function normalizeBigram(s: string): string {
  return s
    .toLowerCase()
    .replace(/[^a-z0-9 ]/g, '')
    .replace(/\s+/g, '')
}

function toBigrams(s: string): string[] {
  const bigrams: string[] = []
  for (let i = 0; i < s.length - 1; i++) {
    bigrams.push(s.substring(i, i + 2))
  }
  return bigrams
}

export function bigramSimilarity(a: string, b: string): number {
  const na = normalizeBigram(a)
  const nb = normalizeBigram(b)
  const aBigrams = toBigrams(na)
  const bBigrams = toBigrams(nb)
  if (aBigrams.length === 0 || bBigrams.length === 0) return 0
  const bSet = new Set(bBigrams)
  let intersection = 0
  for (const bg of aBigrams) {
    if (bSet.has(bg)) intersection++
  }
  return (2 * intersection) / (aBigrams.length + bBigrams.length)
}

export function textRelevance(query: string, name: string): number {
  const q = query.toLowerCase().trim()
  const n = name.toLowerCase().trim()

  if (n === q) return 100

  if (n.startsWith(q)) return 80 + 15 * (q.length / n.length)

  if (n.includes(q)) return 60 + 15 * (q.length / n.length)

  const qTokens = q.split(/\s+/)
  const nTokens = n.split(/\s+/)
  const tokenHits = qTokens.filter(t => nTokens.some(nt => nt.startsWith(t) || nt.includes(t)))

  if (tokenHits.length === qTokens.length) {
    return 40 + 20 * (tokenHits.length / Math.max(nTokens.length, 1))
  }

  if (tokenHits.length > 0) {
    return 20 + 20 * (tokenHits.length / qTokens.length)
  }

  const similarity = bigramSimilarity(q, n)
  if (similarity > 0.3) {
    return similarity * 30
  }

  return 0
}

export function computeRelevanceScore(
  query: string,
  result: LocationResult,
  apiRank?: number,
  totalApiResults?: number
): number {
  const text = textRelevance(query, result.displayName)

  const sourceBoost = SOURCE_BOOSTS[result.source ?? 'photon'] ?? 1.0

  let distanceFactor = 1.0
  if (result.distance !== undefined) {
    distanceFactor = 0.5 + 0.5 / (1 + result.distance / 200)
  }

  let rankFactor = 1.0
  if (apiRank !== undefined && totalApiResults !== undefined && totalApiResults > 1) {
    rankFactor = 1.0 - 0.15 * (apiRank / (totalApiResults - 1))
  }

  return text * sourceBoost * distanceFactor * rankFactor
}

export function sortByRelevance(
  results: LocationResult[],
  query: string,
  apiRankMap?: Map<LocationResult, number>,
  totalApiResults?: number
): LocationResult[] {
  return results
    .map(item => ({
      item,
      score: computeRelevanceScore(query, item, apiRankMap?.get(item), totalApiResults),
    }))
    .sort((a, b) => b.score - a.score)
    .map(s => s.item)
}
